import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaCheck, FaPen, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import { crm } from "../../../theme";
import {
  ProcessPartner,
  ProcessPartnerType,
} from "../../../models/ProcessPartner";
import {
  deleteProcessPartner,
  getProcessPartners,
  saveProcessPartner,
} from "../../../services/processPartnerService";

interface Props {
  type: ProcessPartnerType;
  title: string;
  description: string;
  icon: JSX.Element;
}

// Lista + cadastro inline de armadores / agentes de carga.
// O mesmo componente serve aos dois tipos (só muda o `type`).
const ProcessPartners = ({ type, title, description, icon }: Props) => {
  const [partners, setPartners] = useState<ProcessPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | undefined>();
  const [editingName, setEditingName] = useState("");
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const response = await getProcessPartners(type);
    setPartners(response ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    setSearch("");
    setNewName("");
    setEditingId(undefined);
  }, [type]);

  const filtered = useMemo(() => {
    const term = search.trim().toUpperCase();
    if (!term) return partners;
    return partners.filter((p) => p.name?.toUpperCase().includes(term));
  }, [partners, search]);

  const persist = async (name: string, id?: string) => {
    if (!name.trim()) {
      toast({
        title: "Informe o nome.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    setSaving(true);
    const response = await saveProcessPartner({ id, name: name.trim(), type });
    setSaving(false);

    if (!response?.data?.success) {
      toast({
        title: "Erro ao salvar.",
        description: response?.data?.notifications?.[0],
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    await load();
    toast({
      title: id ? "Cadastro atualizado." : "Cadastro criado.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    return true;
  };

  const add = async () => {
    if (await persist(newName)) setNewName("");
  };

  const confirmEdit = async () => {
    if (await persist(editingName, editingId)) setEditingId(undefined);
  };

  const remove = async (id?: string) => {
    if (!id) return;
    await deleteProcessPartner(id);
    await load();
    toast({
      title: "Cadastro removido.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box>
      <Heading size="lg" mb="24px" fontWeight="normal">
        <Flex alignItems="center" gap="12px">
          {icon} {title}
        </Flex>
      </Heading>

      <Text mb="24px" color={crm.textMuted}>
        {description}
      </Text>

      <Flex gap="12px" mb="24px" flexDir={{ base: "column", md: "row" }}>
        <Input
          placeholder="Nome"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
          borderRadius="10px"
          backgroundColor="white"
        />
        <Button
          leftIcon={<FaPlus />}
          backgroundColor={crm.brand}
          color="white"
          borderRadius="10px"
          minW="140px"
          isLoading={saving}
          _hover={{ backgroundColor: crm.brandDark }}
          onClick={add}
        >
          Adicionar
        </Button>
      </Flex>

      {partners.length > 5 && (
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          mb="16px"
          borderRadius="10px"
          backgroundColor="white"
        />
      )}

      <Flex
        flexDir="column"
        gap="12px"
        backgroundColor="white"
        borderRadius="12px"
        border={`1px solid ${crm.border}`}
        boxShadow={crm.shadowSm}
        p="24px"
      >
        {loading && <Spinner alignSelf="center" />}
        {!loading && filtered.length === 0 && (
          <Text color={crm.textFaint}>
            {partners.length === 0
              ? "Nenhum cadastro ainda."
              : "Nenhum resultado para a busca."}
          </Text>
        )}
        {!loading &&
          filtered.map((p) => (
            <Flex
              key={p.id}
              alignItems="center"
              justifyContent="space-between"
              gap="12px"
              borderWidth="1px"
              borderRadius="md"
              p="12px 16px"
            >
              {editingId === p.id ? (
                <>
                  <Input
                    value={editingName}
                    autoFocus
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmEdit();
                      if (e.key === "Escape") setEditingId(undefined);
                    }}
                    size="sm"
                    borderRadius="8px"
                  />
                  <Flex gap="8px">
                    <IconButton
                      aria-label="Salvar"
                      icon={<FaCheck />}
                      size="sm"
                      colorScheme="green"
                      isLoading={saving}
                      onClick={confirmEdit}
                    />
                    <IconButton
                      aria-label="Cancelar"
                      icon={<FaTimes />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(undefined)}
                    />
                  </Flex>
                </>
              ) : (
                <>
                  <Heading size="sm" fontWeight="semibold">
                    {p.name}
                  </Heading>
                  <Flex gap="8px">
                    <IconButton
                      aria-label="Editar"
                      icon={<FaPen />}
                      size="sm"
                      variant="ghost"
                      color={crm.brand}
                      borderRadius="10px"
                      _hover={{ bg: crm.brandSoft }}
                      onClick={() => {
                        setEditingId(p.id);
                        setEditingName(p.name);
                      }}
                    />
                    <IconButton
                      aria-label="Remover"
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => remove(p.id)}
                    />
                  </Flex>
                </>
              )}
            </Flex>
          ))}
      </Flex>
    </Box>
  );
};

export default ProcessPartners;
